import { SETTINGSSTATE, USERSSTATE } from '.'
import { inChatQuickBuyDialog } from '../actions/inChatQuickBuyDialog'
import { restoreQuickReply } from '../actions/message'
import { UserEvent } from '../constants/enums'
import { _fetchTokenConfig } from '../mfcLogic/api/tokens'
import { restoreRooms, setupInteractionIntervals } from '../mfcLogic/rooms'
import { getUsersState } from './getState'
import { slugToID } from './internal/users'
import { pubSubUsers } from './pubSub'
import { AppRouter } from './state.router'

/**
 * Resolves `true` once the current user is known to be a logged-in member
 * (non-zero id), `false` for guests. Falls back to `false` if no ME_ID
 * arrives within 10s, so callers cannot distinguish guest from timeout.
 * Settles once per page load.
 */
export const meUser = new Promise<boolean>((resolve) => {
    const me = USERSSTATE.users[slugToID['me']]
    if (me && me.id) return resolve(true)

    pubSubUsers.on(UserEvent.ME_ID, (id: number) => {
        if (typeof id !== 'number') return
        resolve(id > 0)
    })
    setTimeout(() => {
        // ensure we eventually return this if this event isnt coming
        resolve(false)
    }, 10000)
})

pubSubUsers.on(
    'UPDATE:tokens',
    ({ current: tokens }: { current: number; id: number }) => {
        const { disableInChatQuickbuy } = SETTINGSSTATE.MY_ACCOUNT

        if (tokens !== 0 || disableInChatQuickbuy) {
            return
        }

        const {
            visibleQuery: { slug: querySlug } = {},
            floatingPage,
            visiblePage,
            slug,
        } = AppRouter.route

        if (
            (slug || querySlug) &&
            (visiblePage === 'room' || visiblePage === 'message')
        ) {
            _fetchTokenConfig().then((config) => {
                inChatQuickBuyDialog({
                    ...config,
                    location: {
                        type: floatingPage,
                        slug,
                    },
                })
            })
        }
    },
)

pubSubUsers.on(UserEvent.MODELS_REQUESTED, () => {
    setUsersModelsRequestedState(true)
})

pubSubUsers.on(UserEvent.MODELS_LOADED, () => {
    setUsersModelsLoadedState(true)
    setupInteractionIntervals()
    restoreQuickReply()
    restoreRooms()
})

const setUsersState = (key, value) => {
    USERSSTATE[key] = value
}

const setUsersModelsLoadedState = (modelsLoaded: boolean) => {
    setUsersState('modelsLoaded', modelsLoaded)
}

const setUsersModelsRequestedState = (modelsRequested: boolean) => {
    setUsersState('modelsRequested', modelsRequested)
}

const waitForInitialModelListClosure = (
    state: {
        resolve: null | ((loaded) => void)
        promise: null | Promise<UserEvent>
    } = {
        resolve: null,
        promise: null,
    },
) => {
    pubSubUsers.on(UserEvent.MODELS_LOADED, () => {
        if (!(state.promise instanceof Promise)) {
            return
        }

        state.promise = null
        state.resolve(true)
        state.resolve = null
    })

    return async () => {
        const { modelsLoaded } = getUsersState()

        if (modelsLoaded) {
            return modelsLoaded
        }

        if (!(state.promise instanceof Promise)) {
            state.promise = new Promise<UserEvent>(
                (resolve) => (state.resolve = resolve),
            )
        }

        return state.promise
    }
}

export const waitForInitialModelList = waitForInitialModelListClosure()

export const onUserAction = (ev: UserEvent) => (payload: unknown) => {
    pubSubUsers.emit(ev, payload, true)
}
